/**
 * Workspace SLA thresholds — persisted via human-agent-service `GET/PUT /api/v1/settings/sla`.
 */

import { workspaceHumanAgentSettingsClient } from "@/api/workspaceHumanAgentSettingsClient";
import { mapSlaFromApi, mapSlaToApi } from "@/api/workspaceHumanAgentSettingsMappers";

/** @typedef {{ chatFirstResponseSeconds: number, chatAverageResponseSeconds: number, ticketFirstResponseSeconds: number, ticketAverageResponseSeconds: number }} SlaSettings */

/** @type {SlaSettings} */
export const DEFAULT_SLA_SETTINGS = {
  chatFirstResponseSeconds: 60,
  chatAverageResponseSeconds: 50,
  ticketFirstResponseSeconds: 3600,
  ticketAverageResponseSeconds: 3600,
};

/** @param {unknown} raw */
function coerceSettings(raw) {
  if (!raw || typeof raw !== "object") return { ...DEFAULT_SLA_SETTINGS };
  return mapSlaFromApi(/** @type {Record<string, unknown>} */ (raw));
}

/**
 * @returns {Promise<{ data: SlaSettings }>}
 */
export async function getSlaSettings() {
  try {
    const raw = await workspaceHumanAgentSettingsClient.getSla();
    return { data: coerceSettings(raw) };
  } catch {
    return { data: { ...DEFAULT_SLA_SETTINGS } };
  }
}

/**
 * @param {Partial<SlaSettings>} patch
 * @returns {Promise<{ data: SlaSettings }>}
 */
export async function saveSlaSettings(patch) {
  const prev = (await getSlaSettings()).data;
  const merged = { ...prev, ...patch };
  const saved = await workspaceHumanAgentSettingsClient.putSla(mapSlaToApi(merged));
  const data = coerceSettings(saved);
  try {
    window.dispatchEvent(new CustomEvent("sla-settings-changed", { detail: data }));
  } catch {
    /* ignore */
  }
  return { data };
}
